/** Shared validation utilities — single source of truth for common field rules. */

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const URL_RE   = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
const PHONE_STRIP_RE = /[\s()\-\.]/g;

/**
 * Detects script/HTML injection in plain-text fields (names, company names, etc.).
 * Checks for:
 *   - Any HTML tag opening:           <script, <img, <svg, <iframe, </anything ...
 *   - Inline event handlers:          onerror=, onclick=, onload= ...
 *   - Dangerous URI schemes:          javascript:, vbscript:, data:
 *   - URL-encoded tag opener:         %3C  (decoded <)
 *   - Unicode escape of tag opener:   \u003c
 *
 * NOTE: email, phone, and URL fields are already guarded by their own strict
 * allow-list regexes, so this check is only needed for free-text fields
 * (firstName, lastName, companyName, invite name, client name, etc.).
 */
const DANGEROUS_RE = /<[a-z\/!]|on[a-z]+=|javascript:|vbscript:|data:|%3c|\u003c/i;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value ?? '');
}

export function isValidPhone(value: string): boolean {
  return /^\d{10}$/.test((value ?? '').replace(PHONE_STRIP_RE, ''));
}

export function hasDangerousContent(value: string): boolean {
  return DANGEROUS_RE.test(value ?? '');
}

export function isValidUrl(value: string): boolean {
  const raw = (value ?? '').trim();
  if (!raw) return false;

  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const { hostname } = new URL(candidate);
    return URL_RE.test(hostname.replace(/^www\./i, ''));
  } catch {
    return false;
  }
}
